import { InlineLoading, InlineNotification, OverflowMenu } from '@carbon/react';
import { useAtom } from 'jotai';
import { capitalize } from 'lodash-es';
import { useState } from 'react';
import { structureGridValidationAtom } from './atoms';
import styles from './structure-grid-validity-popover.module.scss';
import { useValidityStatusConfig } from './validity-status-indicator';

interface StructureGridValidityPopoverProps {
  structureGridId: string;
  validity: 'warning' | 'incomplete' | 'valid' | 'invalid' | null;
}

const StructureGridValidityPopover = ({
  structureGridId,
  validity,
}: StructureGridValidityPopoverProps) => {
  const [structureGridValidation, fetchStructureGridValidation] = useAtom(
    structureGridValidationAtom
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const validityConfig = useValidityStatusConfig(validity);

  const validityStatusText = (
    validity: 'warning' | 'incomplete' | 'valid' | 'invalid' | null
  ) => {
    if (validity === 'warning') {
      return (
        <span className={styles.warningValidityStatus}>Warning:Has Issues</span>
      );
    }
    if (validity === 'incomplete') {
      return (
        <span className={styles.incompleteValidityStatus}>
          Validity Status Incomplete
        </span>
      );
    }
    if (validity === 'valid') {
      return (
        <span className={styles.validValidityStatus}>Valid Surface Status</span>
      );
    }
    if (validity === 'invalid') {
      return (
        <span className={styles.invalidValidityStatus}>
          Invalid Structure Grid
        </span>
      );
    }
    return (
      <span className={styles.unknownValidityStatus}>Validity Unavailable</span>
    );
  };

  const handleMenuOpen = () => {
    setErrorMessage(null); // Clear any previous errors
    fetchStructureGridValidation({
      url: `/queries/structure_grids/validations`,
      type: 'POST',
      data: { structureGrids: [{ id: structureGridId }] },
      onError: (error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load validation information';
        setErrorMessage(errorMessage);
      },
    });
  };

  // Extract validation data from the API response
  const validationResults =
    structureGridValidation.result?.type === 'success'
      ? structureGridValidation.result.data?.results || []
      : [];

  return (
    <div className={styles.validityStatusContainer}>
      {validityStatusText(validity)}
      {validity !== null && (
        <OverflowMenu
          data-testid="structure-grid-validity-overflow-menu"
          renderIcon={() => validityConfig.icon}
          iconDescription="Show surface validity details"
          direction="bottom"
          menuOptionsClass={styles.structureGridValidityPopover}
          size="lg"
          aria-label="Show surface validity details"
          onOpen={handleMenuOpen}
        >
          {structureGridValidation.loading ? (
            <div className={styles.loadingContainer}>
              <InlineLoading
                description="Loading validation details..."
                status="active"
              />
            </div>
          ) : errorMessage ? (
            <div className={styles.errorContainer}>
              <InlineNotification
                kind="error"
                title="Error loading validation details"
                subtitle={errorMessage}
                hideCloseButton
              />
            </div>
          ) : validationResults.length > 0 ? (
            validationResults.map((validationResult, index) => (
              <div key={index} className={styles.validityItem}>
                <span className={styles.label}>
                  {validationResult.displayMetadata?.label || '--'}
                </span>
                <span
                  className={
                    validationResult.displayMetadata?.status?.toLowerCase() ===
                    'failed'
                      ? styles.failedValidityStatus
                      : styles.validityStatus
                  }
                >
                  {validationResult.displayMetadata?.status?.toLowerCase() ===
                  'failed'
                    ? (
                        validationResult.displayMetadata?.status || '--'
                      ).toUpperCase()
                    : capitalize(
                        validationResult.displayMetadata?.status || '--'
                      )}
                </span>
              </div>
            ))
          ) : (
            <div className={styles.loadingContainer}>
              <span>No validation data available</span>
            </div>
          )}
        </OverflowMenu>
      )}
    </div>
  );
};

export default StructureGridValidityPopover;
